// ═══════════════════════════════════════
// ATLAS BEASTS — INVENTORY
// Pokemon Go-style mobile overlay
// ═══════════════════════════════════════

import { useState } from 'react'

const ITEM_CATEGORIES = [
  { id: 'consumables', label: 'Consumables', icon: '🧪', color: '#FF4800' },
  { id: 'materials', label: 'Materials', icon: '💎', color: '#00F0FF' },
  { id: 'equipment', label: 'Equipment', icon: '⚔️', color: '#39FF14' },
  { id: 'special', label: 'Special', icon: '✨', color: '#FFE800' },
]

const SAMPLE_ITEMS = [
  { id: 'health-potion', name: 'Health Potion', category: 'consumables', quantity: 5, rarity: 'common', icon: '🧪', description: 'Restores 50 HP to active character in battle' },
  { id: 'revive', name: 'Revive', category: 'consumables', quantity: 2, rarity: 'uncommon', icon: '💊', description: 'Revives a defeated character with 25% HP' },
  { id: 'fire-essence', name: 'Fire Essence', category: 'materials', quantity: 12, rarity: 'common', icon: '🔥', description: 'Used to craft fire-type equipment and items' },
  { id: 'water-crystal', name: 'Water Crystal', category: 'materials', quantity: 8, rarity: 'uncommon', icon: '💧', description: 'Used to craft water-type equipment and items' },
  { id: 'steel-blade', name: 'Steel Blade', category: 'equipment', quantity: 1, rarity: 'rare', icon: '🗡️', description: '+15 Attack when equipped to a character' },
  { id: 'shield-core', name: 'Shield Core', category: 'equipment', quantity: 1, rarity: 'epic', icon: '🛡️', description: '+20 Defense when equipped to a character' },
  { id: 'mystery-box', name: 'Mystery Box', category: 'special', quantity: 1, rarity: 'legendary', icon: '🎁', description: 'Contains a random rare item or creature' },
]

const RARITY_COLORS = {
  common: '#8A8AA5',
  uncommon: '#00F0FF',
  rare: '#39FF14',
  epic: '#BF00FF',
  legendary: '#FFE800',
}

export default function Inventory() {
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedItem, setSelectedItem] = useState<typeof SAMPLE_ITEMS[0] | null>(null)
  const [items, setItems] = useState(SAMPLE_ITEMS)
  const [useResult, setUseResult] = useState<{ message: string; success: boolean } | null>(null)

  const filteredItems = selectedCategory === 'all' 
    ? items 
    : items.filter(item => item.category === selectedCategory)

  function handleUseItem(item: typeof SAMPLE_ITEMS[0]) {
    if (item.quantity <= 0) {
      setUseResult({ message: 'No items left to use!', success: false })
      return
    }

    // Decrease quantity
    setItems(prev => prev.map(i => 
      i.id === item.id ? { ...i, quantity: i.quantity - 1 } : i
    ))

    // Show result based on item type
    if (item.category === 'consumables') {
      setUseResult({ 
        message: `Used ${item.name}! Effect applied.`, 
        success: true 
      })
    } else if (item.category === 'equipment') {
      setUseResult({ 
        message: `${item.name} equipped! Stats updated.`, 
        success: true 
      })
    } else if (item.category === 'special') {
      setUseResult({ 
        message: `${item.name} opened! You received a rare reward!`, 
        success: true 
      })
    } else {
      setUseResult({ 
        message: `${item.name} is a material. Use it in crafting!`, 
        success: false 
      })
    }

    // Close modal after delay
    setTimeout(() => {
      setSelectedItem(null)
      setUseResult(null)
    }, 2000)
  }

  return (
    <div className="flex-1 flex flex-col" style={{ minHeight: '100vh', background: '#020510' }}>

      {/* Top Bar - Compact */}
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'rgba(58,58,90,0.5)' }}>
        <h1 className="font-bold text-lg uppercase tracking-wider" style={{ color: '#EAF6FF', fontFamily: 'Rajdhani, system-ui' }}>Inventory</h1>
        <div className="flex items-center gap-2">
          <span className="font-bold text-[10px] text-[#FFE800]" style={{ fontFamily: 'Space Mono, monospace' }}>
            {items.length} Items
          </span>
        </div>
      </div>

      {/* Category Filter - Horizontal scroll */}
      <div className="flex gap-1.5 overflow-x-auto no-scrollbar px-4 py-3 border-b" style={{ borderColor: 'rgba(58,58,90,0.3)' }}>
        <button
          onClick={() => setSelectedCategory('all')}
          className={`font-bold text-xs px-3 py-1.5 rounded-full border transition-all bg-transparent cursor-pointer whitespace-nowrap ${
            selectedCategory === 'all'
              ? 'border-[#FFE800] border-opacity-60 text-[#FFE800] bg-[#FFE800] bg-opacity-5'
              : 'border-white border-opacity-10 text-[#8A8AA5] hover:border-opacity-20 hover:text-[#EAF6FF]'
          }`}
          style={{ fontFamily: 'Space Mono, monospace' }}
        >
          All
        </button>
        {ITEM_CATEGORIES.map(cat => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`font-bold text-xs px-3 py-1.5 rounded-full border transition-all bg-transparent cursor-pointer whitespace-nowrap ${
              selectedCategory === cat.id
                ? 'border-[#FFE800] border-opacity-60 text-[#FFE800] bg-[#FFE800] bg-opacity-5'
                : 'border-white border-opacity-10 text-[#8A8AA5] hover:border-opacity-20 hover:text-[#EAF6FF]'
          }`}
            style={{ fontFamily: 'Space Mono, monospace' }}
          >
            {cat.icon} {cat.label}
          </button>
        ))}
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4">
        {filteredItems.length === 0 ? (
          <div className="rounded-xl border p-8 text-center" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
            <p className="text-3xl mb-3">📦</p>
            <p className="font-bold text-[#EAF6FF] mb-2" style={{ fontFamily: 'Rajdhani, system-ui' }}>No items</p>
            <button
              onClick={() => setSelectedCategory('all')}
              className="px-4 py-2 rounded-lg font-bold uppercase tracking-wider text-sm transition-all hover:opacity-80"
              style={{ background: 'rgba(58,58,90,0.3)', color: '#8A8AA5', fontFamily: 'Space Mono, monospace' }}
            >
              Show All
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {filteredItems.map(item => (
              <ItemCard key={item.id} item={item} onSelect={setSelectedItem} />
            ))}
          </div>
        )}
      </div>

      {/* Item Detail Modal */}
      {selectedItem && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(2,5,16,0.9)', backdropFilter: 'blur(8px)' }}>
          <div className="rounded-2xl border p-6 max-w-sm w-full" style={{ background: 'rgba(13,13,21,0.95)', borderColor: 'rgba(58,58,90,0.5)' }}>
            <button
              onClick={() => { setSelectedItem(null); setUseResult(null) }}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-[#8A8AA5] hover:text-[#EAF6FF]"
              style={{ border: '1px solid rgba(58,58,90,0.5)' }}
            >
              ✕
            </button>
            
            <div className="text-center mb-4">
              <span className="text-5xl mb-2 block">{selectedItem.icon}</span>
              <h2 className="font-bold text-xl text-[#EAF6FF] uppercase" style={{ fontFamily: 'Rajdhani, system-ui' }}>
                {selectedItem.name}
              </h2>
              <span 
                className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase mt-2"
                style={{ 
                  color: RARITY_COLORS[selectedItem.rarity as keyof typeof RARITY_COLORS],
                  background: `${RARITY_COLORS[selectedItem.rarity as keyof typeof RARITY_COLORS]}15`,
                  border: `1px solid ${RARITY_COLORS[selectedItem.rarity as keyof typeof RARITY_COLORS]}40`,
                  fontFamily: 'Space Mono, monospace',
                }}
              >
                {selectedItem.rarity}
              </span>
            </div>

            <div className="space-y-3">
              <div className="p-3 rounded-lg" style={{ background: 'rgba(58,58,90,0.2)' }}>
                <p className="text-sm text-[#8A8AA5] mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>Description</p>
                <p className="text-sm text-[#EAF6FF]" style={{ fontFamily: 'Inter, sans-serif' }}>{selectedItem.description}</p>
              </div>
              
              <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: 'rgba(58,58,90,0.2)' }}>
                <span className="text-sm text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>Quantity</span>
                <span className="font-bold text-lg text-[#EAF6FF]" style={{ fontFamily: 'Rajdhani, system-ui' }}>{selectedItem.quantity}</span>
              </div>
              
              <div className="flex justify-between items-center p-3 rounded-lg" style={{ background: 'rgba(58,58,90,0.2)' }}>
                <span className="text-sm text-[#8A8AA5]" style={{ fontFamily: 'Inter, sans-serif' }}>Category</span>
                <span className="font-bold text-sm text-[#EAF6FF]" style={{ fontFamily: 'Rajdhani, system-ui' }}>
                  {ITEM_CATEGORIES.find(c => c.id === selectedItem.category)?.label}
                </span>
              </div>
            </div>

            {useResult && (
              <div className={`mt-4 p-3 rounded-lg text-center text-sm font-bold ${
                useResult.success ? 'bg-[#39FF14] bg-opacity-20 text-[#39FF14]' : 'bg-[#FF4800] bg-opacity-20 text-[#FF4800]'
              }`} style={{ fontFamily: 'Space Mono, monospace' }}>
                {useResult.message}
              </div>
            )}

            <button
              onClick={() => handleUseItem(selectedItem)}
              disabled={selectedItem.quantity <= 0}
              className="w-full mt-4 py-3 rounded-lg font-bold uppercase tracking-wider text-sm transition-all hover:opacity-80 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ 
                background: 'linear-gradient(135deg, #00F0FF 0%, #00F0FF80 100%)',
                color: '#020510',
                fontFamily: 'Space Mono, monospace',
              }}
            >
              {selectedItem.quantity <= 0 ? 'Out of Stock' : 'Use Item'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function ItemCard({ item, onSelect }: { item: typeof SAMPLE_ITEMS[0]; onSelect: (item: typeof SAMPLE_ITEMS[0]) => void }) {
  const rarityColor = RARITY_COLORS[item.rarity as keyof typeof RARITY_COLORS]

  return (
    <button
      onClick={() => onSelect(item)}
      className="rounded-xl border p-4 transition-all active:scale-95 hover:scale-105 text-left"
      style={{ 
        background: 'rgba(13,13,21,0.95)', 
        borderColor: `${rarityColor}40`,
        boxShadow: `0 0 12px ${rarityColor}15`,
      }}
    >
      <div className="text-3xl mb-2">{item.icon}</div>
      <p className="font-bold text-sm text-[#EAF6FF] uppercase leading-tight mb-1" style={{ fontFamily: 'Rajdhani, system-ui' }}>
        {item.name}
      </p>
      <div className="flex items-center justify-between">
        <span 
          className="text-[10px] font-bold uppercase"
          style={{ 
            color: rarityColor,
            fontFamily: 'Space Mono, monospace',
          }}
        >
          {item.rarity}
        </span>
        <span className="font-bold text-xs text-[#8A8AA5]" style={{ fontFamily: 'Space Mono, monospace' }}>
          ×{item.quantity}
        </span>
      </div>
    </button>
  )
}
